/**
 * Sentry Logger Utility
 * Centralized error tracking and logging with user context, data sanitization,
 * and environment-aware behavior.
 *
 * @module utils/sentry-logger
 */
import * as Sentry from '@sentry/vue'

import store from '@/store'

// ============================================================================
// CONSTANTS
// ============================================================================

/** Log status types */
export const LOG_STATUS = Object.freeze({
  SUCCESS: 'success',
  FAILURE: 'failure',
  PENDING: 'pending',
  INFO: 'info',
})

/** Breadcrumb categories */
export const LOG_CATEGORY = Object.freeze({
  AUTH: 'auth',
  API: 'api',
  SECURE_UPLOAD: 'secure-upload',
  NAVIGATION: 'navigation',
  USER_ACTION: 'user-action',
})

/** Sentry severity levels */
export const LOG_LEVEL = Object.freeze({
  DEBUG: 'debug',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  FATAL: 'fatal',
})

/** Keys that should always be redacted from logs */
const SENSITIVE_KEYS = Object.freeze([
  'password',
  'token',
  'access_token',
  'refresh_token',
  'otp',
  'otp_key',
  'secret',
  'authorization',
  'ssn',
  'social_security',
  'credit_card',
  'card_number',
  'cvv',
  'pin',
  'credential',
  'api_key',
  'apikey',
  'private_key',
  'bearer',
])

/** Keys containing PII that can be optionally masked */
const PII_KEYS = Object.freeze(['email', 'phone', 'mobile', 'cell', 'address', 'dob', 'date_of_birth'])

/** WeakSet to track errors that have already been logged to Sentry */
const loggedErrors = new WeakSet()

/**
 * Check if an error has already been logged to Sentry
 * @param {Error} error - The error to check
 * @returns {boolean}
 */
export const isErrorLogged = (error) => {
  return error && typeof error === 'object' && loggedErrors.has(error)
}

/**
 * Mark an error as logged to prevent duplicate Sentry events
 * @param {Error} error - The error to mark
 */
export const markErrorLogged = (error) => {
  if (error && typeof error === 'object') {
    loggedErrors.add(error)
  }
}

// ============================================================================
// INTERNAL UTILITIES
// ============================================================================

/**
 * Check if Sentry is properly initialized
 * @returns {boolean}
 */
const isSentryEnabled = () => {
  try {
    return !!Sentry.getClient?.()
  } catch {
    return false
  }
}

/**
 * Get current timestamp in ISO format
 * @returns {string}
 */
const getTimestamp = () => new Date().toISOString()

/**
 * Safely access store state with fallback
 * @param {string} path - Dot-notation path (e.g., 'globalComponents.userId')
 * @param {*} fallback - Default value if path doesn't exist
 * @returns {*}
 */
const getStoreValue = (path, fallback = null) => {
  try {
    return path.split('.').reduce((obj, key) => obj?.[key], store?.state) ?? fallback
  } catch {
    return fallback
  }
}

/**
 * Extract HTTP error code from various error/response objects
 * @param {Object} data - Data object that may contain error info
 * @returns {number|string|null} - HTTP status code or null
 */
const extractErrorCode = (data = {}) => {
  if (!data || typeof data !== 'object') {
    return null
  }

  return (
    data.statusCode ||
    data.status ||
    data.serverResponse?.status ||
    data.serverResponse?.statusCode ||
    data.response?.status ||
    data.error?.status ||
    data.error?.statusCode ||
    data.code ||
    null
  )
}

/**
 * Format error details for consistent logging
 * @param {Object} data - Error data object
 * @returns {Object} - Formatted error details
 */
const formatErrorDetails = (data = {}) => {
  const errorCode = extractErrorCode(data)

  return {
    errorCode,
    errorMessage: data.error || data.message || data.serverResponse?.message || data.errorMessage,
    serverResponse: data.serverResponse,
    httpStatus: errorCode,
    timestamp: getTimestamp(),
  }
}

/**
 * Get current user context from store/session
 * @returns {Object} User context object
 */
const getUserContext = () => ({
  id: getStoreValue('globalComponents.userId', 'anonymous'),
  email: getStoreValue('globalComponents.userEmail', 'unknown'),
  role: getStoreValue('globalComponents.role', 'unknown'),
  isSecureUploadLogin: getStoreValue('globalComponents.isSecureUploadLogin', false),
  isAuthenticated: getStoreValue('auth.isAuth', false),
})

/**
 * Convert status to Sentry level
 * @param {string} status
 * @returns {string}
 */
const statusToLevel = (status) => (status === LOG_STATUS.FAILURE ? LOG_LEVEL.ERROR : LOG_LEVEL.INFO)

/**
 * Generic event logger - reduces duplication across log functions
 * Only logs to Sentry on failure/error status to reduce noise
 * @param {Object} config - Logger configuration
 */
const logEvent = ({ category, action, data = {}, status = LOG_STATUS.INFO, contextName, extraContext = {} }) => {
  // Only log failures/errors to Sentry, skip success and info events
  if (!isSentryEnabled() || status === LOG_STATUS.SUCCESS || status === LOG_STATUS.INFO) {
    return
  }

  const userContext = getUserContext()
  const sanitizedData = sanitizeData(data)
  const errorDetails = formatErrorDetails(data)
  const level = statusToLevel(status)

  // Add breadcrumb for error context
  Sentry.addBreadcrumb({
    category,
    message: `${category}: ${action}`,
    level,
    data: {
      action,
      status,
      errorCode: errorDetails.errorCode,
      userId: userContext.id,
      timestamp: getTimestamp(),
      ...sanitizedData,
    },
  })

  // Set context if provided
  if (contextName) {
    Sentry.setContext(contextName, {
      lastAction: action,
      status,
      userId: userContext.id,
      errorCode: errorDetails.errorCode,
      timestamp: getTimestamp(),
      ...extraContext,
      ...sanitizedData,
    })
  }

  // Capture error events
  if (status === LOG_STATUS.FAILURE) {
    Sentry.captureMessage(`${category.toUpperCase()} Error: ${action} (${errorDetails.errorCode || 'Unknown'})`, {
      level: LOG_LEVEL.ERROR,
      tags: {
        category,
        action,
        errorCode: String(errorDetails.errorCode || 'unknown'),
      },
      extra: {
        action,
        userId: userContext.id,
        userEmail: userContext.email,
        errorCode: errorDetails.errorCode,
        httpStatus: errorDetails.httpStatus,
        timestamp: getTimestamp(),
        ...extraContext,
        ...sanitizedData,
      },
    })
  }
}

// ============================================================================
// PUBLIC API - USER CONTEXT
// ============================================================================

/**
 * Set Sentry user context (call after successful login)
 * @param {Object} userData - User data to set
 * @param {string} [userData.id] - User ID
 * @param {string} [userData.email] - User email
 * @param {string} [userData.username] - Username
 * @param {string} [userData.role] - User role
 */
export const setSentryUser = (userData = {}) => {
  if (!isSentryEnabled()) {
    return
  }

  const context = getUserContext()

  Sentry.setUser({
    id: userData.id || context.id,
    email: userData.email || context.email,
    username: userData.username || userData.name || context.email,
    role: userData.role || context.role,
  })
}

/**
 * Clear Sentry user context (call on logout)
 */
export const clearSentryUser = () => {
  if (!isSentryEnabled()) {
    return
  }
  Sentry.setUser(null)
}

/**
 * Set additional context for Sentry events
 * @param {string} contextName - Name of the context
 * @param {Object} data - Context data
 */
export const setSentryContext = (contextName, data) => {
  if (!isSentryEnabled()) {
    return
  }
  Sentry.setContext(contextName, data)
}

// ============================================================================
// PUBLIC API - BREADCRUMBS
// ============================================================================

/**
 * Add a breadcrumb for user action tracking
 * Only logs warning and error levels to Sentry to reduce noise
 * @param {string} category - Category (use LOG_CATEGORY constants)
 * @param {string} message - Description of the action
 * @param {Object} [data={}] - Additional data
 * @param {string} [level='info'] - Severity level (use LOG_LEVEL constants)
 */
export const addSentryBreadcrumb = (category, message, data = {}, level = LOG_LEVEL.INFO) => {
  // Only log warning and error levels, skip info/debug
  if (!isSentryEnabled() || level === LOG_LEVEL.INFO || level === LOG_LEVEL.DEBUG) {
    return
  }

  const userContext = getUserContext()

  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data: {
      ...sanitizeData(data),
      userId: userContext.id,
      timestamp: getTimestamp(),
    },
  })
}

// ============================================================================
// PUBLIC API - DOMAIN-SPECIFIC LOGGERS
// ============================================================================

/**
 * Log authentication events with enhanced context
 * @param {string} action - Auth action (e.g., 'login', 'logout', 'otp-verify')
 * @param {Object} [data={}] - Additional data (error, serverResponse, statusCode)
 * @param {string} [status='info'] - Event status (use LOG_STATUS constants)
 */
export const logAuthEvent = (action, data = {}, status = LOG_STATUS.INFO) => {
  logEvent({
    category: LOG_CATEGORY.AUTH,
    action,
    data,
    status,
    contextName: 'auth_event',
  })
}

/**
 * Log secure upload events with enhanced context
 * @param {string} action - Upload action (e.g., 'document-upload', 'verify-otp')
 * @param {Object} [data={}] - Additional data (serverResponse, statusCode)
 * @param {string} [status='info'] - Event status (use LOG_STATUS constants)
 */
export const logSecureUploadEvent = (action, data = {}, status = LOG_STATUS.INFO) => {
  const secureMemberData = getStoreValue('globalComponents.secureMemberData')
  const memberId = secureMemberData?.member?.id || data.memberId

  logEvent({
    category: LOG_CATEGORY.SECURE_UPLOAD,
    action,
    data,
    status,
    contextName: 'secure_upload_event',
    extraContext: {
      memberId,
      isSecureUploadLogin: getUserContext().isSecureUploadLogin,
    },
  })
}

/**
 * Log API call events
 * @param {string} endpoint - API endpoint URL
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {Object} [data={}] - Request/response data
 * @param {string} [status='success'] - Event status (use LOG_STATUS constants)
 */
export const logApiEvent = (endpoint, method, data = {}, status = LOG_STATUS.SUCCESS) => {
  logEvent({
    category: LOG_CATEGORY.API,
    action: `${method} ${endpoint}`,
    data,
    status,
    extraContext: { endpoint, method },
  })
}

/**
 * Log user navigation events
 * @param {string} from - Previous route name/path
 * @param {string} to - Current route name/path
 * @param {Object} [params={}] - Route params
 */
export const logNavigationEvent = (from, to, params = {}) => {
  addSentryBreadcrumb(LOG_CATEGORY.NAVIGATION, `Navigate: ${from} → ${to}`, {
    from,
    to,
    params: sanitizeData(params),
  })
}

/**
 * Log generic user actions
 * @param {string} action - Action description
 * @param {Object} [data={}] - Additional context
 */
export const logUserAction = (action, data = {}) => {
  addSentryBreadcrumb(LOG_CATEGORY.USER_ACTION, action, data)
}

// ============================================================================
// PUBLIC API - EXCEPTION HANDLING
// ============================================================================

/**
 * Capture an exception with enhanced context
 * @param {Error} error - The error to capture
 * @param {Object} [context={}] - Additional context
 * @param {string} [context.action] - Action that caused the error
 */
export const captureExceptionWithContext = (error, context = {}) => {
  if (!isSentryEnabled()) {
    return
  }

  const userContext = getUserContext()

  Sentry.withScope((scope) => {
    scope.setUser({
      id: userContext.id,
      email: userContext.email,
      role: userContext.role,
    })

    scope.setContext('error_context', {
      ...sanitizeData(context),
      timestamp: getTimestamp(),
      isAuthenticated: userContext.isAuthenticated,
      isSecureUploadLogin: userContext.isSecureUploadLogin,
    })

    scope.setTag('user_role', userContext.role)
    scope.setTag('is_authenticated', String(userContext.isAuthenticated))

    if (context.action) {
      scope.setTag('action', context.action)
    }

    Sentry.captureException(error)
  })
}

/**
 * Capture a message with enhanced context
 * Only captures warning, error, and fatal levels to reduce noise
 * @param {string} message - Message to capture
 * @param {string} [level='info'] - Severity level (use LOG_LEVEL constants)
 * @param {Object} [context={}] - Additional context
 */
export const captureMessageWithContext = (message, level = LOG_LEVEL.INFO, context = {}) => {
  // Only capture warning, error, and fatal levels
  if (!isSentryEnabled() || level === LOG_LEVEL.INFO || level === LOG_LEVEL.DEBUG) {
    return
  }

  const userContext = getUserContext()

  Sentry.withScope((scope) => {
    scope.setUser({
      id: userContext.id,
      email: userContext.email,
      role: userContext.role,
    })

    scope.setContext('message_context', {
      ...sanitizeData(context),
      timestamp: getTimestamp(),
    })

    Sentry.captureMessage(message, level)
  })
}

// ============================================================================
// PUBLIC API - DATA SANITIZATION
// ============================================================================

/**
 * Sentry-safe sanitization for arbitrary string values.
 * Best-effort redaction of secrets even when data is not valid JSON.
 *
 * @param {string} str
 * @param {Object} [options={}]
 * @param {boolean} [options.maskPII=false]
 * @returns {string}
 * @private
 */
const sanitizeStringValue = (str, options = {}) => {
  const { maskPII = false } = options
  let result = String(str)

  // Redact values associated with known sensitive keys in "key=value" patterns
  if (Array.isArray(SENSITIVE_KEYS) && SENSITIVE_KEYS.length > 0) {
    for (const key of SENSITIVE_KEYS) {
      if (!key) {
        continue
      }
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const eqPattern = new RegExp(`(${escapedKey})=([^&\\s]+)`, 'ig')
      result = result.replace(eqPattern, (_match, k) => `${k}=[REDACTED]`)

      const colonPattern = new RegExp(`(${escapedKey})\\s*:\\s*([^;\\n\\r]+)`, 'ig')
      result = result.replace(colonPattern, (_match, k) => `${k}: [REDACTED]`)
    }
  }

  // Best-effort PII masking (e.g., email addresses) when enabled
  if (maskPII) {
    const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
    result = result.replace(emailPattern, '***@***')
  }

  return result
}

/**
 * Sanitize sensitive data before logging
 * Removes passwords, tokens, and other sensitive information
 *
 * @param {*} data - Data to sanitize (object, string, or primitive)
 * @param {Object} [options={}] - Sanitization options
 * @param {boolean} [options.maskPII=false] - Whether to mask PII fields
 * @param {number} [options.maxDepth=10] - Maximum recursion depth
 * @returns {*} Sanitized data
 *
 * @example
 * sanitizeData({ password: 'secret', email: 'test@example.com' })
 * // Returns: { password: '[REDACTED]', email: 'test@example.com' }
 *
 * @example
 * sanitizeData({ email: 'test@example.com' }, { maskPII: true })
 * // Returns: { email: 'tes***' }
 */
export const sanitizeData = (data, options = {}, depth = 0) => {
  const { maskPII = false, maxDepth = 10 } = options

  // Prevent infinite recursion
  if (depth > maxDepth) {
    return '[MAX_DEPTH_EXCEEDED]'
  }

  // Handle null/undefined
  if (data === null) {
    return null
  }

  // Handle strings - try to parse JSON
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return sanitizeData(parsed, options, depth)
    } catch {
      // If not valid JSON, apply best-effort redaction instead of returning raw data
      return sanitizeStringValue(data, { maskPII })
    }
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeData(item, options, depth + 1))
  }

  // Handle non-objects
  if (typeof data !== 'object') {
    return data
  }

  // Handle objects
  const sanitized = {}

  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase()

    // Check for sensitive keys
    if (SENSITIVE_KEYS.some((sensitive) => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]'
      continue
    }

    // Check for PII keys
    if (maskPII && PII_KEYS.some((pii) => lowerKey.includes(pii)) && typeof value === 'string') {
      sanitized[key] = value.length > 3 ? `${value.substring(0, 3)}***` : '***'
      continue
    }

    // Recursively sanitize nested objects
    if (value !== null && typeof value === 'object') {
      sanitized[key] = sanitizeData(value, options, depth + 1)
      continue
    }

    sanitized[key] = value
  }

  return sanitized
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Constants
  LOG_STATUS,
  LOG_CATEGORY,
  LOG_LEVEL,

  // User context
  setSentryUser,
  clearSentryUser,
  setSentryContext,

  // Breadcrumbs
  addSentryBreadcrumb,

  // Domain loggers
  logAuthEvent,
  logSecureUploadEvent,
  logApiEvent,
  logNavigationEvent,
  logUserAction,

  // Exception handling
  captureExceptionWithContext,
  captureMessageWithContext,

  // Utilities
  sanitizeData,
  isErrorLogged,
  markErrorLogged,
}
